import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractsApi, type ContractSub, type ContractOwner } from '@/lib/apiClient';
import { Button, StateView } from '@/components';
import ContractsStyles from './ContractsStyles';
import ContractsBoard from './ContractsBoard';
import SubcontractModal from './SubcontractModal';
import OwnerContractModal from './OwnerContractModal';
import JobSubcontractRequests from './JobSubcontractRequests';

/**
 * One job's slice of Contracts, in the project workspace: its subcontracts (all
 * of them — a job's own history is small) and any owner contract linked to it.
 * The same board as the /contracts page, without the Job column.
 *
 * Someone who may start a subcontract request (#627) also gets a Request subcontract
 * button and their own requests on this job, drafts included. A request is the form a
 * project manager fills in; the coordinator turns it into the subcontract row below.
 *
 * Keyed by project in the workspace, so an open dialog can never carry from one
 * job to the next.
 */
export default function ContractsTab({ projectId, jobLabel }: { projectId: number; jobLabel: string }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [subModal, setSubModal] = useState<{ row?: ContractSub } | null>(null);
  const [ownerModal, setOwnerModal] = useState<{ row?: ContractOwner } | null>(null);

  const q = useQuery({
    queryKey: ['contracts', 'project', projectId],
    queryFn: () => contractsApi.forProject(projectId),
    staleTime: 30_000,
  });
  const canEdit = q.data?.access.canEdit ?? false;
  const canRequest = q.data?.access.canRequest ?? false;
  // A job whose number is a placeholder (SYSTAC-, ClosedWon-, IMP-, MNT-) is not one a
  // NEW subcontract is booked to. Owner contracts are not held back, and rows already on
  // the job stay editable.
  const placeholder = q.data?.access.placeholderNumber ?? false;
  // An archived job takes nothing new (the API refuses it, as the job picker leaves such
  // jobs out); what is already on it stays editable.
  const archived = q.data?.access.archived ?? false;
  const fixedJob = { id: projectId, label: jobLabel };

  return (
    <>
      <ContractsStyles />

      <div className="ct-toolbar">
        <h2 className="ct-toolbar-title">Subcontracts and owner contracts</h2>
        <input
          type="search"
          placeholder="Search vendor, subcontract #"
          aria-label="Search this job’s contracts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canRequest && (
          // The API refuses a request on a placeholder-number or archived job, exactly as it refuses + Subcontract.
          <Button variant="primary" onClick={() => navigate(`/contracts/requests/new/${projectId}`)} disabled={placeholder || archived}>
            Request subcontract
          </Button>
        )}
        {canEdit && (
          <>
            <Button variant="secondary" onClick={() => setOwnerModal({})} disabled={archived}>+ Owner contract</Button>
            <Button variant={canRequest ? 'secondary' : 'primary'} onClick={() => setSubModal({})} disabled={placeholder || archived}>+ Subcontract</Button>
          </>
        )}
      </div>

      {/* Archived first: nothing new goes on it at all, so the placeholder note's "an owner contract can" would be untrue. */}
      {archived ? (
        <div className="ct-hint" role="note">
          This job is archived, so a new subcontract or owner contract can’t be added to it. What’s already here can still be updated.
        </div>
      ) : placeholder ? (
        <div className="ct-hint" role="note">
          This job doesn’t have a real job number from accounting, so a new subcontract can’t be added to it. An owner contract can.
        </div>
      ) : null}

      {canRequest && <JobSubcontractRequests projectId={projectId} />}

      <StateView query={q}>
        {(data) => (
          <ContractsBoard
            subcontracts={data.subcontracts}
            ownerContracts={data.ownerContracts}
            search={search}
            canEdit={canEdit}
            showJob={false}
            onEditSub={(row) => setSubModal({ row })}
            onEditOwner={(row) => setOwnerModal({ row })}
          />
        )}
      </StateView>

      {subModal && <SubcontractModal row={subModal.row} fixedJob={fixedJob} onClose={() => setSubModal(null)} />}
      {ownerModal && <OwnerContractModal row={ownerModal.row} fixedJob={fixedJob} onClose={() => setOwnerModal(null)} />}
    </>
  );
}
